import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

type UserRole = 'admin' | 'supervisor' | 'operator' | 'viewer' | 'quality' | 'warehouse' | 'maintenance' | null;

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Roles permitidos para acceder a esta ruta */
  allowedRoles?: UserRole[];
  /** Modulo requerido para acceder a esta ruta */
  requiredModule?: string;
  /** Redirigir a esta ruta si no tiene permisos */
  redirectTo?: string;
}

export const ProtectedRoute = ({ 
  children, 
  allowedRoles,
  requiredModule,
  redirectTo 
}: ProtectedRouteProps) => {
  const { user, userRole, loading } = useAuth();
  const { canAccessModule } = useModuleAccess();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Verificar roles si se especificaron
  if (allowedRoles && allowedRoles.length > 0) {
    const hasPermission = allowedRoles.includes(userRole as UserRole);
    
    if (!hasPermission) {
      // Redirigir si se especificó una ruta
      if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
      }
      
      // Mostrar mensaje de acceso denegado
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 rounded-full bg-destructive/10 w-fit">
                <ShieldAlert className="w-8 h-8 text-destructive" />
              </div>
              <CardTitle className="text-xl">Acceso Denegado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <p className="text-muted-foreground">
                No tienes permisos para acceder a esta página.
              </p>
              <p className="text-sm text-muted-foreground">
                Tu rol actual: <strong className="text-foreground">{userRole || 'Sin rol'}</strong>
              </p>
              <Button onClick={() => window.history.back()} variant="outline">
                Volver
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  if (requiredModule && !canAccessModule(requiredModule)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-destructive/10 w-fit">
              <ShieldAlert className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-xl">Acceso Denegado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-muted-foreground">
              No tienes permisos para acceder a este modulo.
            </p>
            <p className="text-sm text-muted-foreground">
              Tu rol actual: <strong className="text-foreground">{userRole || 'Sin rol'}</strong>
            </p>
            <Button onClick={() => window.history.back()} variant="outline">
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
